import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, FileText, Printer, Edit3, Trash2, Search,
  CheckCircle, AlertTriangle, X, ChevronDown, Loader2, Upload,
  Scissors, ChevronRight, Package, Eye, CheckSquare, Copy
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const ORANGE = '#e87722';

// ── PDF generation (lazy import) ─────────────────────────────────────────────

// Format ISO date string as DD/MM/YY
const fmtDDMMYY = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00'); // T00:00:00 prevents timezone shift
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

async function buildSheetPDF(stockTake, sheet, products) {
  const { default: jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const sheetProducts = products.filter(p => sheet.product_codes.includes(p.product_code));
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // ── Header ──────────────────────────────────────────────────────────────────
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Stock Take Count Sheet', 15, 18);

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Date', pageW - 80, 18);
  doc.setFont('helvetica', 'normal');
  doc.text(fmtDDMMYY(stockTake.date), pageW - 65, 18);

  // Counter Name row
  doc.setFont('helvetica', 'bold');
  doc.text('Counter Name', 15, 28);
  doc.setDrawColor(180);
  doc.line(50, 28, pageW - 15, 28);

  // Count Sheet ID row
  doc.setFont('helvetica', 'bold');
  doc.text('Count Sheet ID', 15, 38);
  doc.setFont('helvetica', 'normal');
  doc.text(sheet.name, 50, 38);

  // Divider
  doc.setDrawColor(200);
  doc.setLineWidth(0.4);
  doc.line(15, 42, pageW - 15, 42);

  // ── Product table ────────────────────────────────────────────────────────────
  const colWidths = {
    0: { cellWidth: 38 },   // Product Code
    1: { cellWidth: 80 },   // Product Description
    2: { cellWidth: 26 },   // Stocking Unit
    3: { cellWidth: 35 },   // Quantity Counted
    4: { cellWidth: 'auto' }, // Note
  };

  autoTable(doc, {
    startY: 47,
    head: [['Product Code', 'Product Description', 'Stocking Unit', 'Quantity Counted', 'Note']],
    body: sheetProducts.map(p => [
      p.product_code,
      p.product_description,
      p.stock_unit || p.stocking_unit || '',
      '',
      '',
    ]),
    columnStyles: colWidths,
    headStyles: {
      fontStyle: 'bold',
      fontSize: 12,
      fillColor: [230, 230, 230],
      textColor: [30, 30, 30],
      halign: 'center',
      lineWidth: 0.3,
    },
    bodyStyles: {
      fontSize: 10,
      minCellHeight: 10,
      lineWidth: 0.2,
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 15, right: 15 },
  });

  // Page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150);
    doc.text(`${stockTake.name} — ${sheet.name} — Page ${i} of ${pageCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    doc.setTextColor(0);
  }

  return doc;
}

async function buildAllSheetsPDF(stockTake, products) {
  const { default: jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  let isFirstSheet = true;

  for (const sheet of stockTake.sheets) {
    if (!isFirstSheet) doc.addPage();
    isFirstSheet = false;

    const sheetProducts = products.filter(p => sheet.product_codes.includes(p.product_code));

    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Stock Take Count Sheet', 15, 18);

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Date', pageW - 80, 18);
    doc.setFont('helvetica', 'normal');
    doc.text(fmtDDMMYY(stockTake.date), pageW - 65, 18);

    doc.setFont('helvetica', 'bold');
    doc.text('Counter Name', 15, 28);
    doc.setDrawColor(180);
    doc.line(50, 28, pageW - 15, 28);

    doc.setFont('helvetica', 'bold');
    doc.text('Count Sheet ID', 15, 38);
    doc.setFont('helvetica', 'normal');
    doc.text(sheet.name, 50, 38);

    doc.setDrawColor(200);
    doc.setLineWidth(0.4);
    doc.line(15, 42, pageW - 15, 42);

    autoTable(doc, {
      startY: 47,
      head: [['Product Code', 'Product Description', 'Stocking Unit', 'Quantity Counted', 'Note']],
      body: sheetProducts.map(p => [
        p.product_code,
        p.product_description,
        p.stock_unit || p.stocking_unit || '',
        '',
        '',
      ]),
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 80 },
        2: { cellWidth: 26 },
        3: { cellWidth: 35 },
        4: { cellWidth: 'auto' },
      },
      headStyles: { fontStyle: 'bold', fontSize: 12, fillColor: [230, 230, 230], textColor: [30, 30, 30], halign: 'center', lineWidth: 0.3 },
      bodyStyles: { fontSize: 10, minCellHeight: 10, lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      margin: { left: 15, right: 15 },
    });
  }

  // Page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150);
    doc.text(`${stockTake.name} — Page ${i} of ${pageCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    doc.setTextColor(0);
  }

  return doc;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function StocktakeView({ activeStockTakeId, allStockTakes = [], onDataLoaded, onRequestNewStocktake, user, onDeleted }) {
  const [activeStockTake, setActiveStockTake] = useState(null);
  const [loading, setLoading] = useState(false);

  // Product table state
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [lastCheckedIndex, setLastCheckedIndex] = useState(-1);

  // Edit sheet state
  const [editingSheetId, setEditingSheetId] = useState(null);
  const [editCodes, setEditCodes] = useState(new Set());
  const [lastEditCheckedIndex, setLastEditCheckedIndex] = useState(-1);

  // Search — kept for scrolling context (replaced by filteredProducts for filtering)

  // Confirm print all
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);

  // Delete stock take confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Copy sheets modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceId, setCopySourceId] = useState('');
  const [copying, setCopying] = useState(false);

  // Free Stock Report modal
  const [showFreeStockModal, setShowFreeStockModal] = useState(false);
  const [freeStockAssignments, setFreeStockAssignments] = useState({}); // code → sheetId
  const [savingFreeStock, setSavingFreeStock] = useState(false);
  const [freeStockSearch, setFreeStockSearch] = useState('');

  // Inline neighbor-assign dropdown (clickable "—" dash)
  const [dashMenuCode, setDashMenuCode] = useState(null); // product code whose dash is open

  // Sheet action dropdown
  const [openSheetMenu, setOpenSheetMenu] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null); // for fixed-position overflow-safe dropdown

  // Product table filter
  const [productSearch, setProductSearch] = useState('');
  const [showAssigned, setShowAssigned] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const rowRefs = useRef({});

  // ── Load stock take ─────────────────────────────────────────────────────────
  const load = useCallback(async (id) => {
    if (!id) { setActiveStockTake(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`${API}/stock-takes/${id}`);
      setActiveStockTake(r.data.stock_take);
    } catch {
      toast.error('Failed to load stock take');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(activeStockTakeId); }, [activeStockTakeId, load]);

  const products = activeStockTake?.products || [];
  const sheets = activeStockTake?.sheets || [];

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getProductSheet = (code) => sheets.find(s => s.product_codes.includes(code)) || null;

  const getUnassignedCount = () => products.filter(p => !getProductSheet(p.product_code)).length;

  // Extract prefix: leading digits + following letters (e.g. "03C" from "03C017020", "06BA" from "06BA12...")
  const getProductPrefix = (code) => {
    const m = (code || '').match(/^(\d*[A-Za-z]+)/);
    return m ? m[1].toUpperCase() : (code || '').slice(0, 3).toUpperCase();
  };

  // Auto-generate sheet name from selected codes (e.g. "03C – 03CZ")
  const getSheetNameFromCodes = (codes) => {
    const sorted = [...codes].sort();
    const first = getProductPrefix(sorted[0]);
    const last = getProductPrefix(sorted[sorted.length - 1]);
    return first === last ? `${first} – ${first}Z` : `${first} – ${last}Z`;
  };

  // Reset selection anchor when filters change
  useEffect(() => { setLastCheckedIndex(-1); }, [productSearch, showAssigned]);

  // Filtered products for the table
  const filteredProducts = useMemo(() => {
    const assignedCodes = new Set(sheets.flatMap(s => s.product_codes));
    let list = showAssigned ? [...products] : products.filter(p => !assignedCodes.has(p.product_code));
    if (productSearch.trim()) {
      const q = productSearch.trim().toLowerCase();
      list = list.filter(p =>
        (p.product_code || '').toLowerCase().includes(q) ||
        (p.product_description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, sheets, showAssigned, productSearch]);

  // For each unassigned product in the current filtered view (when showAssigned=true),
  // compute the nearest assigned sheet above and below it in the list order.
  const neighborSheetMap = useMemo(() => {
    if (!showAssigned) return {};
    const codeToSheet = {};
    sheets.forEach(s => s.product_codes.forEach(c => { codeToSheet[c] = s; }));
    const result = {};
    for (let i = 0; i < filteredProducts.length; i++) {
      const code = filteredProducts[i].product_code;
      if (codeToSheet[code]) continue; // already assigned
      let above = null, below = null;
      for (let j = i - 1; j >= 0 && !above; j--) {
        above = codeToSheet[filteredProducts[j].product_code] || null;
      }
      for (let j = i + 1; j < filteredProducts.length && !below; j++) {
        below = codeToSheet[filteredProducts[j].product_code] || null;
      }
      const options = [];
      if (above) options.push(above);
      if (below && (!above || below.id !== above.id)) options.push(below);
      if (options.length > 0) result[code] = options;
    }
    return result;
  }, [filteredProducts, sheets, showAssigned]);

  // ── Checkbox shift-click ────────────────────────────────────────────────────
  const handleCheck = (visIdx, code, withShift) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (withShift && lastCheckedIndex >= 0) {
        const lo = Math.min(visIdx, lastCheckedIndex);
        const hi = Math.max(visIdx, lastCheckedIndex);
        const shouldAdd = !prev.has(code);
        for (let i = lo; i <= hi; i++) {
          const c = filteredProducts[i]?.product_code;
          if (c) shouldAdd ? next.add(c) : next.delete(c);
        }
      } else {
        prev.has(code) ? next.delete(code) : next.add(code);
      }
      return next;
    });
    setLastCheckedIndex(visIdx);
  };

  // ── Create sheet ────────────────────────────────────────────────────────────
  const handleCreateSheet = async () => {
    if (!selectedCodes.size) return;
    const name = getSheetNameFromCodes(selectedCodes);
    try {
      const r = await api.post(`${API}/stock-takes/${activeStockTake.id}/sheets`, {
        name,
        product_codes: Array.from(selectedCodes),
      });
      setActiveStockTake(prev => ({
        ...prev,
        sheets: [...(prev.sheets || []), r.data.sheet],
      }));
      setSelectedCodes(new Set());
      toast.success(`${r.data.sheet.name} created with ${selectedCodes.size} products`);
    } catch {
      toast.error('Failed to create count sheet');
    }
  };

  // ── Delete sheet ────────────────────────────────────────────────────────────
  const handleDeleteSheet = async (sheetId) => {
    try {
      await api.delete(`${API}/stock-takes/${activeStockTake.id}/sheets/${sheetId}`);
      setActiveStockTake(prev => ({
        ...prev,
        sheets: prev.sheets.filter(s => s.id !== sheetId),
      }));
      toast.success('Sheet deleted');
    } catch {
      toast.error('Failed to delete sheet');
    }
  };

  // ── Edit sheet helpers ───────────────────────────────────────────────────────
  const startEdit = (sheet) => {
    setEditingSheetId(sheet.id);
    setEditCodes(new Set(sheet.product_codes));
    setSelectedCodes(new Set());
    setLastEditCheckedIndex(-1);
    // When editing, show assigned products so the sheet's products are visible
    setShowAssigned(true);
    // Scroll to first product in sheet
    const firstIdx = products.findIndex(p => sheet.product_codes.includes(p.product_code));
    if (firstIdx >= 0) {
      setTimeout(() => {
        const el = rowRefs.current[firstIdx];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const saveEdit = async () => {
    const sheet = sheets.find(s => s.id === editingSheetId);
    if (!sheet) return;
    try {
      await api.put(`${API}/stock-takes/${activeStockTake.id}/sheets/${editingSheetId}`, {
        name: sheet.name,
        product_codes: Array.from(editCodes),
      });
      setActiveStockTake(prev => ({
        ...prev,
        sheets: prev.sheets.map(s => s.id === editingSheetId ? { ...s, product_codes: Array.from(editCodes) } : s),
      }));
      setEditingSheetId(null);
      setLastEditCheckedIndex(-1);
      setShowAssigned(false);
      toast.success('Sheet updated');
    } catch {
      toast.error('Failed to save sheet');
    }
  };

  const cancelEdit = () => { setEditingSheetId(null); setEditCodes(new Set()); setLastEditCheckedIndex(-1); setShowAssigned(false); };

  // ── Delete entire stock take ──────────────────────────────────────────────
  const handleDeleteStockTake = async () => {
    if (!deleteConfirmChecked) return;
    setDeleting(true);
    try {
      await api.delete(`${API}/stock-takes/${activeStockTake.id}`);
      toast.success(`"${activeStockTake.name}" deleted`);
      setShowDeleteConfirm(false);
      setDeleteConfirmChecked(false);
      onDeleted?.(activeStockTake.id);
    } catch {
      toast.error('Failed to delete stock take');
    } finally {
      setDeleting(false);
    }
  };

  // ── Free Stock Report ────────────────────────────────────────────────────
  const unassignedProducts = useMemo(() => {
    const assignedCodes = new Set(sheets.flatMap(s => s.product_codes));
    return products.filter(p => !assignedCodes.has(p.product_code));
  }, [products, sheets]);

  const openFreeStockModal = () => {
    setFreeStockAssignments({});
    setFreeStockSearch('');
    setShowFreeStockModal(true);
  };

  const saveFreeStockAssignments = async () => {
    const entries = Object.entries(freeStockAssignments).filter(([, sheetId]) => !!sheetId);
    if (!entries.length) { toast('No assignments to save'); return; }
    setSavingFreeStock(true);
    try {
      const updatedSheets = sheets.map(s => {
        const toAdd = entries.filter(([, sid]) => sid === s.id).map(([code]) => code);
        return { ...s, product_codes: [...s.product_codes, ...toAdd] };
      });
      await api.put(`${API}/stock-takes/${activeStockTake.id}`, { sheets: updatedSheets });
      setActiveStockTake(prev => ({ ...prev, sheets: updatedSheets }));
      setShowFreeStockModal(false);
      toast.success(`${entries.length} product${entries.length !== 1 ? 's' : ''} assigned to groups`);
    } catch {
      toast.error('Failed to save assignments');
    } finally {
      setSavingFreeStock(false);
    }
  };

  // ── Copy sheets from another stock take ──────────────────────────────────
  const handleCopySheets = async () => {
    if (!copySourceId) return;
    setCopying(true);
    try {
      const r = await api.post(`${API}/stock-takes/${activeStockTake.id}/copy-sheets`, {
        source_stock_take_id: copySourceId,
      });
      const newSheets = r.data.sheets || [];
      setActiveStockTake(prev => ({
        ...prev,
        sheets: [...(prev.sheets || []), ...newSheets],
      }));
      setShowCopyModal(false);
      setCopySourceId('');
      toast.success(`${r.data.copied_count} count sheet${r.data.copied_count !== 1 ? 's' : ''} copied successfully`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to copy sheets');
    } finally {
      setCopying(false);
    }
  };

  // Shift-click support for edit mode
  const handleEditCheck = (visIdx, code, withShift) => {
    setEditCodes(prev => {
      const next = new Set(prev);
      if (withShift && lastEditCheckedIndex >= 0) {
        const lo = Math.min(visIdx, lastEditCheckedIndex);
        const hi = Math.max(visIdx, lastEditCheckedIndex);
        const shouldAdd = !prev.has(code);
        for (let i = lo; i <= hi; i++) {
          const c = filteredProducts[i]?.product_code;
          if (c) shouldAdd ? next.add(c) : next.delete(c);
        }
      } else {
        prev.has(code) ? next.delete(code) : next.add(code);
      }
      return next;
    });
    setLastEditCheckedIndex(visIdx);
  };

  // ── PDF actions ──────────────────────────────────────────────────────────────
  const handleViewSheet = async (sheet) => {
    try {
      const doc = await buildSheetPDF(activeStockTake, sheet, products);
      const blob = doc.output('blob');
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error('PDF generation failed');
    }
  };

  const handlePrintSheet = async (sheet) => {
    try {
      const doc = await buildSheetPDF(activeStockTake, sheet, products);
      doc.autoPrint();
      const blob = doc.output('bloburl');
      window.open(blob, '_blank');
    } catch {
      toast.error('Print failed');
    }
  };

  const handlePrintAll = async () => {
    if (!sheets.length) return;
    setPrintingAll(true);
    setShowPrintConfirm(false);
    try {
      const doc = await buildAllSheetsPDF(activeStockTake, products);
      doc.autoPrint();
      const blob = doc.output('bloburl');
      window.open(blob, '_blank');
    } catch {
      toast.error('Print all failed');
    } finally {
      setPrintingAll(false);
    }
  };

  // ── Assign individual product to a sheet ────────────────────────────────────
  const assignToSheet = async (productCode, targetSheetId) => {
    const updatedSheets = sheets.map(s => {
      const codes = s.product_codes.filter(c => c !== productCode);
      if (s.id === targetSheetId) codes.push(productCode);
      return { ...s, product_codes: codes };
    });
    try {
      await api.put(`${API}/stock-takes/${activeStockTake.id}`, { sheets: updatedSheets });
      setActiveStockTake(prev => ({ ...prev, sheets: updatedSheets }));
    } catch {
      toast.error('Failed to assign product');
    }
  };

  // ── No active stock take ─────────────────────────────────────────────────────
  if (!activeStockTakeId && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6" data-testid="stocktake-empty">
        <div className="w-20 h-20 rounded-full bg-orange-50 border-2 border-orange-200 flex items-center justify-center">
          <ClipboardList className="w-10 h-10 text-orange-400" />
        </div>
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-700">No Stock Take Selected</h3>
          <p className="text-gray-400 mt-1 text-sm">Start a new stock take or select an existing one from the dropdown above.</p>
        </div>
        <button
          onClick={onRequestNewStocktake}
          className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white shadow-lg transition-all hover:opacity-90 active:scale-95"
          style={{ backgroundColor: ORANGE }}
          data-testid="start-stocktake-btn"
        >
          <Plus className="w-4 h-4" /> Start Stock Take Count Sheets
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  if (!activeStockTake) return null;

  const isEditing = !!editingSheetId;
  const editingSheet = sheets.find(s => s.id === editingSheetId);

  return (
    <div className="flex flex-col h-full" data-testid="stocktake-view">
      {/* ── Count sheet cards ──────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide">
            Count Sheets ({sheets.length}) — {getUnassignedCount()} products unassigned
          </h3>
          <div className="flex items-center gap-2">
            {/* Free Stock Report — only when sheets exist and there are unassigned products */}
            {sheets.length > 0 && unassignedProducts.length > 0 && (
              <button
                onClick={openFreeStockModal}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors"
                style={{ borderColor: ORANGE, color: ORANGE }}
                data-testid="free-stock-report-btn"
                title="View and assign unassigned products to existing groups"
              >
                <Package className="w-3.5 h-3.5" />
                Free Stock ({unassignedProducts.length})
              </button>
            )}
            {/* Copy sheets from previous stock take */}
            {allStockTakes.filter(st => st.id !== activeStockTake?.id).length > 0 && (
              <button
                onClick={() => { setShowCopyModal(true); setCopySourceId(''); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors"
                style={{ borderColor: ORANGE, color: ORANGE }}
                data-testid="copy-count-sheets-btn"
                title="Copy count groups from a previous stock take"
              >
                <Copy className="w-3.5 h-3.5" /> Copy Count Sheets
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmChecked(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                data-testid="delete-stocktake-btn"
                title="Delete this stock take (super admin only)"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete Stock Take
              </button>
            )}
            <button
              onClick={() => setShowPrintConfirm(true)}
              disabled={!sheets.length || printingAll}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors disabled:opacity-40"
              style={{ borderColor: ORANGE, color: ORANGE }}
              data-testid="print-all-btn"
            >
              {printingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              Print All
            </button>
          </div>
        </div>

        {sheets.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center py-5 text-sm text-gray-400">
            No count sheets yet — select products below and click "Create Count Sheet"
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex gap-3 overflow-x-auto p-3 scrollbar-thin">
            {sheets.map((sheet) => {
              const cnt = sheet.product_codes.length;
              const isThisEditing = editingSheetId === sheet.id;
              return (
                <div
                  key={sheet.id}
                  className={`flex-shrink-0 w-44 rounded-xl border-2 bg-white shadow-sm transition-all ${isThisEditing ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-200 hover:border-orange-300'}`}
                  data-testid={`sheet-card-${sheet.id}`}
                >
                  <div className="p-3 pb-2">
                    <div className="flex items-start justify-between gap-1">
                      <div>
                        <p className="text-xs font-bold text-gray-800 leading-tight">{sheet.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{cnt} product{cnt !== 1 ? 's' : ''}</p>
                      </div>
                      {isThisEditing && <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Editing</span>}
                    </div>
                  </div>
                  <div className="border-t border-gray-100 flex">
                    <button
                      onClick={() => startEdit(sheet)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors rounded-bl-xl"
                      title="Edit sheet"
                    >
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    <div className="w-px bg-gray-100" />
                    <button
                      onClick={() => handleViewSheet(sheet)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                      title="View PDF"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                    <div className="w-px bg-gray-100" />
                    <button
                      onClick={(e) => {
                        if (openSheetMenu === sheet.id) {
                          setOpenSheetMenu(null);
                          setDropdownPos(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                          setOpenSheetMenu(sheet.id);
                        }
                      }}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors rounded-br-xl"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {/* ── Search & filter bar ───────────────────────────────────────────────── */}
      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Filter by code, prefix or description…"
            value={productSearch}
            onChange={e => setProductSearch(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 outline-none"
            style={{ '--tw-ring-color': ORANGE }}
            data-testid="product-search-input"
          />
          {productSearch && (
            <button onClick={() => setProductSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowAssigned(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
            showAssigned
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
          }`}
          data-testid="toggle-assigned-btn"
          title={showAssigned ? 'Click to hide assigned products' : 'Click to show all products including assigned'}
        >
          <CheckSquare className="w-3.5 h-3.5" />
          {showAssigned ? 'Showing All' : 'Unassigned Only'}
        </button>
        <span className="text-xs text-gray-400">
          {filteredProducts.length} of {products.length}
        </span>
      </div>

      {/* ── Product table ─────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm border-collapse" data-testid="product-table">
          <thead className="sticky top-0 bg-white border-b-2 border-gray-200 z-10">
            <tr>
              <th className="w-10 px-3 py-3 text-center">
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 accent-orange-500"
                  checked={filteredProducts.length > 0 && filteredProducts.filter(p => !getProductSheet(p.product_code)).every(p => selectedCodes.has(p.product_code))}
                  onChange={e => {
                    const unassigned = filteredProducts.filter(p => !getProductSheet(p.product_code));
                    if (e.target.checked) {
                      setSelectedCodes(prev => new Set([...prev, ...unassigned.map(p => p.product_code)]));
                    } else {
                      const toRemove = new Set(unassigned.map(p => p.product_code));
                      setSelectedCodes(prev => new Set([...prev].filter(c => !toRemove.has(c))));
                    }
                  }}
                  title="Select all visible unassigned"
                />
              </th>
              <th className="px-3 py-3 text-left font-semibold text-gray-700">Product Code</th>
              <th className="px-3 py-3 text-left font-semibold text-gray-700">Product Description</th>
              <th className="px-3 py-3 text-center font-semibold text-gray-700 w-28">
                Assigned Sheet
              </th>
              <th className="w-10 px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product, visIdx) => {
              const code = product.product_code;
              const assignedSheet = getProductSheet(code);
              const isSelected = isEditing ? editCodes.has(code) : selectedCodes.has(code);

              return (
                <tr
                  key={code}
                  ref={el => rowRefs.current[visIdx] = el}
                  className={`border-b border-gray-100 transition-colors ${
                    isSelected ? 'bg-blue-50' :
                    assignedSheet ? 'bg-gray-50/60' :
                    'hover:bg-gray-50'
                  }`}
                  data-testid={`product-row-${code}`}
                >
                  <td className="px-3 py-2.5 text-center">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 accent-orange-500"
                      checked={isSelected}
                      onChange={() => {}}
                      onClick={e => {
                        e.stopPropagation();
                        if (isEditing) {
                          handleEditCheck(visIdx, code, e.shiftKey);
                        } else {
                          handleCheck(visIdx, code, e.shiftKey);
                        }
                      }}
                    />
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs font-semibold text-gray-700">{code}</td>
                  <td className="px-3 py-2.5 text-gray-600">{product.product_description}</td>
                  <td className="px-3 py-2.5 text-center">
                    {assignedSheet ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                        {assignedSheet.name}
                      </span>
                    ) : neighborSheetMap[code]?.length > 0 ? (
                      /* Clickable dash — shows nearest group(s) above / below */
                      <div className="relative inline-block">
                        <button
                          type="button"
                          onClick={() => setDashMenuCode(prev => prev === code ? null : code)}
                          className="px-2 py-0.5 rounded-full text-xs font-semibold border border-dashed border-orange-300 text-orange-400 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-colors"
                          title="Click to assign to nearest group"
                          data-testid={`dash-assign-${code}`}
                        >
                          —
                        </button>
                        {dashMenuCode === code && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setDashMenuCode(null)} />
                            <div className="absolute z-50 top-full mt-1 left-1/2 -translate-x-1/2 w-44 rounded-xl border border-orange-100 bg-white shadow-xl overflow-hidden">
                              <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide border-b border-gray-100">Assign to group</p>
                              {neighborSheetMap[code].map((s, idx) => (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => {
                                    assignToSheet(code, s.id);
                                    setDashMenuCode(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-orange-50 transition-colors"
                                  data-testid={`dash-assign-option-${code}-${s.id}`}
                                >
                                  <span className="text-gray-400 text-[10px]">{idx === 0 ? '↑' : '↓'}</span>
                                  <span className="font-semibold truncate">{s.name}</span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    {sheets.length > 0 && !isEditing && (
                      <div className="relative group">
                        <button
                          className="p-1 rounded hover:bg-orange-100 opacity-0 group-hover:opacity-100 transition-all"
                          title="Assign to sheet"
                          onClick={(e) => {
                            if (openSheetMenu === `assign-${code}`) {
                              setOpenSheetMenu(null); setDropdownPos(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                              setOpenSheetMenu(`assign-${code}`);
                            }
                          }}
                        >
                          <Scissors className="w-3.5 h-3.5 text-orange-400" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Fixed-position dropdown portal (overflow-safe for both card menu and assign menu) ── */}
      {openSheetMenu && dropdownPos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpenSheetMenu(null); setDropdownPos(null); }} />
          {/* Sheet card action menu */}
          {!openSheetMenu.startsWith('assign-') && (
            <div
              className="fixed z-50 w-36 rounded-xl border bg-white shadow-xl overflow-hidden"
              style={{ top: dropdownPos.top, right: dropdownPos.right }}
            >
              {(() => {
                const sheet = sheets.find(s => s.id === openSheetMenu);
                if (!sheet) return null;
                return (
                  <>
                    <button onClick={() => { handlePrintSheet(sheet); setOpenSheetMenu(null); setDropdownPos(null); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50">
                      <Printer className="w-3.5 h-3.5" /> Print
                    </button>
                    <button onClick={() => { handleDeleteSheet(sheet.id); setOpenSheetMenu(null); setDropdownPos(null); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" /> Delete Sheet
                    </button>
                  </>
                );
              })()}
            </div>
          )}
          {/* Assign-to-sheet menu */}
          {openSheetMenu.startsWith('assign-') && (
            <div
              className="fixed z-50 w-40 rounded-xl border bg-white shadow-xl overflow-hidden"
              style={{ top: dropdownPos.top, right: dropdownPos.right }}
            >
              <p className="px-3 py-2 text-xs font-bold text-gray-500 border-b">Assign to sheet</p>
              {sheets.map(s => (
                <button
                  key={s.id}
                  onClick={() => {
                    const code = openSheetMenu.replace('assign-', '');
                    assignToSheet(code, s.id);
                    setOpenSheetMenu(null);
                    setDropdownPos(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-orange-50"
                >
                  <ChevronRight className="w-3 h-3 text-orange-400" /> {s.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Floating action bar — editing mode ───────────────────────────────── */}
      {isEditing && (
        <div className="sticky bottom-4 px-4 flex justify-center pointer-events-none z-20">
          <div className="pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-full text-sm font-bold shadow-2xl bg-amber-500 text-white">
            <Edit3 className="w-4 h-4 flex-shrink-0" />
            <span className="font-semibold">
              Editing <em className="not-italic font-bold">{editingSheet?.name}</em>
              <span className="ml-2 text-amber-100 font-normal text-xs">— {editCodes.size} product{editCodes.size !== 1 ? 's' : ''} selected</span>
            </span>
            <div className="w-px bg-amber-400 h-5 mx-1" />
            <button
              onClick={saveEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white text-amber-600 hover:bg-amber-50 transition-colors"
              data-testid="save-edit-btn"
            >
              <CheckCircle className="w-3.5 h-3.5" /> Save Changes
            </button>
            <button
              onClick={cancelEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-400 text-white hover:bg-amber-600 border border-amber-300 transition-colors"
              data-testid="cancel-edit-btn"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Floating "Create Count Sheet" button ──────────────────────────────── */}
      {!isEditing && selectedCodes.size > 0 && (
        <div className="sticky bottom-4 px-4 flex justify-center pointer-events-none z-20">
          <button
            onClick={handleCreateSheet}
            className="pointer-events-auto flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-sm shadow-2xl transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: ORANGE }}
            data-testid="create-sheet-btn"
          >
            <Plus className="w-4 h-4" />
            Create Count Sheet ({selectedCodes.size} product{selectedCodes.size !== 1 ? 's' : ''} selected)
          </button>
        </div>
      )}

      {/* ── Print All confirm dialog ──────────────────────────────────────────── */}
      {showPrintConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Print All Count Sheets?</h3>
              <p className="text-sm text-gray-500">
                This will combine all {sheets.length} count sheet{sheets.length !== 1 ? 's' : ''} into a single PDF and open the print dialog.
              </p>
            </div>
            <div className="border-t border-gray-100 flex">
              <button onClick={() => setShowPrintConfirm(false)} className="flex-1 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <div className="w-px bg-gray-100" />
              <button
                onClick={handlePrintAll}
                className="flex-1 py-3 text-sm font-bold text-white transition-colors"
                style={{ backgroundColor: ORANGE }}
                data-testid="confirm-print-all-btn"
              >
                Print All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Stock Take confirm dialog ──────────────────────────────────── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="delete-stocktake-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Delete Stock Take?</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  You are about to permanently delete <span className="font-semibold text-gray-700">"{activeStockTake?.name}"</span> and all its count sheets. This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Confirmation checkbox */}
            <div className="px-6 pb-5">
              <label
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors select-none ${
                  deleteConfirmChecked ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-200'
                }`}
                data-testid="delete-confirm-checkbox-label"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 accent-red-500 flex-shrink-0"
                  checked={deleteConfirmChecked}
                  onChange={e => setDeleteConfirmChecked(e.target.checked)}
                  data-testid="delete-confirm-checkbox"
                />
                <span className="text-sm text-gray-700">
                  I understand this will permanently delete this stock take and all associated count sheets.
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 flex">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmChecked(false); }}
                className="flex-1 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <div className="w-px bg-gray-100" />
              <button
                onClick={handleDeleteStockTake}
                disabled={!deleteConfirmChecked || deleting}
                className="flex-1 py-3 text-sm font-bold text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting…' : 'Delete Stock Take'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Copy Count Sheets modal ───────────────────────────────────────────── */}
      {showCopyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="copy-sheets-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Copy className="w-5 h-5" style={{ color: ORANGE }} />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900">Copy Count Sheets</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Select a previous stock take to copy its count groups into this one.
                  Only products that exist in the current stock take will be included.
                  Any products not previously grouped will remain unassigned.
                </p>
              </div>
              <button
                onClick={() => setShowCopyModal(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Stock take picker */}
            <div className="px-6 pb-5 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Select stock take to copy from</p>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {allStockTakes
                  .filter(st => st.id !== activeStockTake?.id)
                  .map(st => (
                    <label
                      key={st.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors select-none ${
                        copySourceId === st.id
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200 hover:bg-orange-50/40'
                      }`}
                      data-testid={`copy-source-option-${st.id}`}
                    >
                      <input
                        type="radio"
                        name="copy-source"
                        value={st.id}
                        checked={copySourceId === st.id}
                        onChange={() => setCopySourceId(st.id)}
                        className="w-4 h-4 flex-shrink-0"
                        style={{ accentColor: ORANGE }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{st.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {st.date ? new Date(st.date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                          {st.sheet_count != null ? ` · ${st.sheet_count} sheet${st.sheet_count !== 1 ? 's' : ''}` : ''}
                        </p>
                      </div>
                    </label>
                  ))}
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 flex">
              <button
                onClick={() => { setShowCopyModal(false); setCopySourceId(''); }}
                className="flex-1 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                data-testid="copy-sheets-cancel-btn"
              >
                Cancel
              </button>
              <div className="w-px bg-gray-100" />
              <button
                onClick={handleCopySheets}
                disabled={!copySourceId || copying}
                className="flex-1 py-3 text-sm font-bold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: copySourceId && !copying ? ORANGE : undefined }}
                data-testid="copy-sheets-confirm-btn"
              >
                {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                {copying ? 'Copying…' : 'Copy Sheets'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Free Stock Report modal ──────────────────────────────────────────── */}
      {showFreeStockModal && (() => {
        const filtered = freeStockSearch.trim()
          ? unassignedProducts.filter(p =>
              p.product_code.toLowerCase().includes(freeStockSearch.trim().toLowerCase()) ||
              (p.product_description || '').toLowerCase().includes(freeStockSearch.trim().toLowerCase())
            )
          : unassignedProducts;
        const assignedCount = Object.values(freeStockAssignments).filter(Boolean).length;

        return (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm pt-8 p-4" data-testid="free-stock-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[88vh]">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ORANGE}18` }}>
                    <Package className="w-4 h-4" style={{ color: ORANGE }} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Free Stock Report</p>
                    <p className="text-xs text-gray-400">
                      {unassignedProducts.length} product{unassignedProducts.length !== 1 ? 's' : ''} not yet in any group
                      {assignedCount > 0 && <span className="ml-1.5 font-semibold" style={{ color: ORANGE }}>· {assignedCount} assigned</span>}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowFreeStockModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="free-stock-modal-close">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Toolbar — search + bulk assign */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3 flex-shrink-0">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filter products…"
                    value={freeStockSearch}
                    onChange={e => setFreeStockSearch(e.target.value)}
                    className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': ORANGE }}
                    data-testid="free-stock-search"
                  />
                  {freeStockSearch && (
                    <button onClick={() => setFreeStockSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-500 font-medium">Assign all to:</span>
                  <select
                    onChange={e => {
                      if (!e.target.value) return;
                      const sheetId = e.target.value;
                      const bulk = {};
                      unassignedProducts.forEach(p => { bulk[p.product_code] = sheetId; });
                      setFreeStockAssignments(bulk);
                      e.target.value = '';
                    }}
                    defaultValue=""
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': ORANGE }}
                    data-testid="free-stock-bulk-assign"
                  >
                    <option value="">Pick group…</option>
                    {sheets.map(s => <option key={s.id} value={s.id}>{s.name} ({s.product_codes.length})</option>)}
                  </select>
                  {assignedCount > 0 && (
                    <button
                      onClick={() => setFreeStockAssignments({})}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1.5 rounded border border-gray-200 hover:border-red-200"
                      data-testid="free-stock-clear-btn"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Product list */}
              <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Package className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-sm">{freeStockSearch ? 'No matching products' : 'All products are assigned to groups!'}</p>
                  </div>
                ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-white border-b border-gray-100 z-10">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Product Code</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide w-44">Assign to Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(product => {
                        const assigned = freeStockAssignments[product.product_code];
                        return (
                          <tr key={product.product_code}
                            className={`border-b border-gray-50 transition-colors ${assigned ? 'bg-orange-50/40' : 'hover:bg-gray-50/60'}`}
                            data-testid={`free-stock-row-${product.product_code}`}
                          >
                            <td className="px-4 py-2.5 font-mono text-xs font-bold text-gray-700">{product.product_code}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-600">{product.product_description}</td>
                            <td className="px-4 py-2.5 text-right">
                              <select
                                value={freeStockAssignments[product.product_code] || ''}
                                onChange={e => setFreeStockAssignments(prev => ({ ...prev, [product.product_code]: e.target.value }))}
                                className={`text-xs rounded-lg px-2 py-1 border outline-none transition-colors ${
                                  assigned
                                    ? 'border-orange-400 bg-orange-50 text-orange-700 font-semibold'
                                    : 'border-gray-200 text-gray-600 bg-white'
                                }`}
                                data-testid={`free-stock-select-${product.product_code}`}
                              >
                                <option value="">—</option>
                                {sheets.map(s => (
                                  <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
                <span className="text-xs text-gray-400">
                  {assignedCount > 0
                    ? <span className="font-semibold" style={{ color: ORANGE }}>{assignedCount} of {unassignedProducts.length} products queued for assignment</span>
                    : `${unassignedProducts.length} unassigned product${unassignedProducts.length !== 1 ? 's' : ''}`
                  }
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFreeStockModal(false)}
                    className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    data-testid="free-stock-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveFreeStockAssignments}
                    disabled={!assignedCount || savingFreeStock}
                    className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: assignedCount && !savingFreeStock ? ORANGE : undefined, background: assignedCount && !savingFreeStock ? ORANGE : '#d1d5db' }}
                    data-testid="free-stock-save-btn"
                  >
                    {savingFreeStock ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                    {savingFreeStock ? 'Saving…' : `Update Groups${assignedCount > 0 ? ` (${assignedCount})` : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
